import React from 'react';
import { useTheme } from '../../context/ThemeContext';
import { formatResetDate } from '../../utils/quota';
import { ArrowRightIcon, ZapIcon } from '../icons';
import type { QuotaExceededPart } from './types';

// Replaces the answer when the monthly advanced-model quota is used up. Never switches
// model on its own: the user picks the smaller model, and the same question is resent.
interface QuotaNudgeProps {
  part: QuotaExceededPart;
  onContinue?: (fallbackModel: string) => void;
  onChooseModel?: () => void;
}

const QuotaNudge: React.FC<QuotaNudgeProps> = ({ part, onContinue, onChooseModel }) => {
  const { colors, theme } = useTheme();
  const accent = theme === 'dark' ? '#fbbf24' : '#b45309';
  const resetDate = formatResetDate(part.resets_at);

  return (
    <div data-testid="quota-nudge" style={{
      margin: '4px 0',
      backgroundColor: colors.surface,
      borderRadius: '12px',
      overflow: 'hidden',
      border: `1px solid ${colors.border}`,
    }}>
      <div style={{ padding: '14px 16px', display: 'flex', alignItems: 'flex-start', gap: '12px' }}>
        <span style={{
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'center',
          flexShrink: 0,
          width: '32px',
          height: '32px',
          borderRadius: '8px',
          color: accent,
          backgroundColor: theme === 'dark' ? 'rgba(245, 158, 11, 0.14)' : 'rgba(245, 158, 11, 0.10)',
        }}>
          <ZapIcon size={18} />
        </span>

        <div style={{ flex: 1, minWidth: 0 }}>
          <div style={{ fontWeight: 600, fontSize: '14px', color: colors.textPrimary }}>
            You've used all your advanced requests for this month
          </div>
          <div style={{ fontSize: '13px', color: colors.textSecondary, marginTop: '3px', lineHeight: '1.55' }}>
            <strong style={{ color: colors.textPrimary }}>{part.model}</strong> is an advanced model, and you've
            reached this month's limit of {part.limit} requests.
            {part.fallback_model && (
              <>
                {' '}You can keep going right now with{' '}
                <strong style={{ color: colors.textPrimary }}>{part.fallback_model}</strong>, which has no monthly limit.
              </>
            )}
          </div>

          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '8px', marginTop: '12px' }}>
            {part.fallback_model && onContinue && (
              <button
                onClick={() => onContinue(part.fallback_model!)}
                style={{
                  padding: '7px 13px',
                  fontSize: '13px',
                  fontWeight: 500,
                  backgroundColor: colors.primary,
                  color: '#fff',
                  border: 'none',
                  borderRadius: '8px',
                  cursor: 'pointer',
                  display: 'flex',
                  alignItems: 'center',
                  gap: '6px',
                }}
              >
                Continue with {part.fallback_model}
                <ArrowRightIcon size={14} />
              </button>
            )}
            {onChooseModel && (
              <button
                onClick={onChooseModel}
                style={{
                  padding: '7px 13px',
                  fontSize: '13px',
                  fontWeight: 500,
                  backgroundColor: 'transparent',
                  color: colors.textPrimary,
                  border: `1px solid ${colors.border}`,
                  borderRadius: '8px',
                  cursor: 'pointer',
                }}
              >
                Choose another model
              </button>
            )}
          </div>
        </div>
      </div>

      <div style={{
        padding: '8px 16px',
        borderTop: `1px solid ${colors.borderLight}`,
        backgroundColor: colors.surfaceElevated,
        display: 'flex',
        alignItems: 'center',
        gap: '10px',
        fontSize: '12px',
        color: colors.textMuted,
      }}>
        <div style={{ flex: '0 0 120px', height: '5px', borderRadius: '3px', backgroundColor: colors.border, overflow: 'hidden' }}>
          <div style={{ width: '100%', height: '100%', backgroundColor: '#ef4444' }} />
        </div>
        {part.used} / {part.limit} advanced requests used · resets {resetDate}
      </div>
    </div>
  );
};

export default QuotaNudge;
